import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { AppointmentType, getAppointmentTypeLabel } from '../constants/appointmentTypes';
import { Calendar, dateFnsLocalizer, View, SlotInfo } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay, addWeeks, subWeeks, addMonths, subMonths, addDays, subDays } from 'date-fns';
import { enUS } from 'date-fns/locale/en-US';
import 'react-big-calendar/lib/css/react-big-calendar.css';

const locales = { 'en-US': enUS };

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 0 }),
  getDay,
  locales,
});

export interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  allDay?: boolean;
  resource?: {
    type: 'appointment' | 'busy' | 'gcal' | 'selected' | 'available';
    status?: string;
    clientName?: string;
    appointmentId?: string;
    googleMeetLink?: string;
    hasSession?: boolean;
    appointmentType?: AppointmentType;
  };
}

interface Props {
  events: CalendarEvent[];
  onSelectSlot: (slotInfo: SlotInfo) => void;
  onSelectEvent: (event: CalendarEvent) => void;
  onRightClickEvent?: (event: CalendarEvent, x: number, y: number) => void;
  onDeleteEvent?: (event: CalendarEvent) => void;
  defaultView?: View;
  /** Called whenever the displayed date changes (navigation or Today). */
  onDateChange?: (date: Date) => void;
  /** Lock to a specific view, hiding the view switcher. */
  lockedView?: View;
  /** Scroll the time grid to show this time at the top. Defaults to 8am. */
  scrollToTime?: Date;
  /** Initial date the calendar opens on. Defaults to today. */
  defaultDate?: Date;
}

const DEFAULT_SCROLL = new Date(1970, 1, 1, 8, 0, 0); // 8am

const CoachCalendarView: React.FC<Props> = ({
  events,
  onSelectSlot,
  onSelectEvent,
  onRightClickEvent,
  onDeleteEvent,
  defaultView = 'week',
  onDateChange,
  lockedView,
  scrollToTime,
  defaultDate,
}) => {
  const [view, setView] = useState<View>(lockedView ?? defaultView);
  const [date, setDate] = useState(defaultDate ?? new Date());

  const handleNavigate = (newDate: Date) => {
    setDate(newDate);
    onDateChange?.(newDate);
  };

  const navigatePrev = () => {
    const newDate = view === 'week' ? subWeeks(date, 1)
      : view === 'month' ? subMonths(date, 1)
      : view === 'day' ? subDays(date, 1)
      : subWeeks(date, 1);
    setDate(newDate);
    onDateChange?.(newDate);
  };

  const navigateNext = () => {
    const newDate = view === 'week' ? addWeeks(date, 1)
      : view === 'month' ? addMonths(date, 1)
      : view === 'day' ? addDays(date, 1)
      : addWeeks(date, 1);
    setDate(newDate);
    onDateChange?.(newDate);
  };

  const dateLabel = useMemo(() => {
    if (view === 'week') {
      const start = startOfWeek(date, { weekStartsOn: 0 });
      const end = addDays(start, 6);
      if (format(start, 'MMM yyyy') === format(end, 'MMM yyyy'))
        return format(start, 'MMMM yyyy');
      if (format(start, 'yyyy') === format(end, 'yyyy'))
        return `${format(start, 'MMM')} – ${format(end, 'MMM yyyy')}`;
      return `${format(start, 'MMM yyyy')} – ${format(end, 'MMM yyyy')}`;
    }
    if (view === 'day') return format(date, 'EEEE, MMMM d, yyyy');
    return format(date, 'MMMM yyyy');
  }, [date, view]);

  const eventStyleGetter = useCallback((event: CalendarEvent) => {
    const type = event.resource?.type;
    const status = event.resource?.status;

    // Selected/picked slot — bright indigo with pulse border
    if (type === 'selected') {
      return {
        style: {
          backgroundColor: '#6366f1',
          border: '2px solid #a5b4fc',
          borderRadius: '6px',
          color: 'white',
          fontSize: '12px',
          fontWeight: '600',
          padding: '2px 6px',
          boxShadow: '0 0 0 3px rgba(99,102,241,0.3)',
        },
      };
    }

    if (type === 'available') {
      return {
        style: {
          backgroundColor: 'rgba(16,185,129,0.2)',
          border: '1px solid rgba(16,185,129,0.6)',
          borderRadius: '6px',
          color: '#6ee7b7',
          fontSize: '11px',
          fontWeight: '500',
          cursor: 'pointer',
        },
      };
    }

    if (type === 'busy') {
      return {
        style: {
          backgroundColor: 'rgba(239,68,68,0.25)',
          border: '1px solid rgba(239,68,68,0.5)',
          color: '#fca5a5',
          borderRadius: '6px',
          fontSize: '11px',
        },
      };
    }

    // Google Calendar events — teal
    if (type === 'gcal') {
      return {
        style: {
          backgroundColor: '#0ea5e9',
          border: 'none',
          borderRadius: '6px',
          color: 'white',
          fontSize: '12px',
          fontWeight: '500',
          padding: '2px 6px',
          opacity: 0.85,
        },
      };
    }

    let bg = '#6366f1'; // indigo default (CoachSight appointments)
    if (status === 'completed') bg = '#10b981';
    if (status === 'cancelled') bg = '#6b7280';
    if (status === 'pending') bg = '#f59e0b';

    return {
      style: {
        backgroundColor: bg,
        border: 'none',
        borderRadius: '6px',
        color: 'white',
        fontSize: '12px',
        fontWeight: '500',
        padding: '2px 6px',
      },
    };
  }, []);

  const formats = useMemo(() => ({
    timeGutterFormat: (date: Date) => format(date, 'h a'),
    eventTimeRangeFormat: ({ start, end }: { start: Date; end: Date }) =>
      `${format(start, 'h:mm')}–${format(end, 'h:mm a')}`,
    dayHeaderFormat: (date: Date) => format(date, 'EEE M/d'),
    monthHeaderFormat: (date: Date) => format(date, 'MMMM yyyy'),
  }), []);

  // Use refs so CustomEvent never changes reference (avoids calendar remount on every render)
  const onRightClickRef = useRef(onRightClickEvent);
  const onSelectEventRef = useRef(onSelectEvent);
  const onDeleteEventRef = useRef(onDeleteEvent);
  useEffect(() => { onRightClickRef.current = onRightClickEvent; }, [onRightClickEvent]);
  useEffect(() => { onSelectEventRef.current = onSelectEvent; }, [onSelectEvent]);
  useEffect(() => { onDeleteEventRef.current = onDeleteEvent; }, [onDeleteEvent]);

  const CustomEvent = useCallback(({ event }: { event: CalendarEvent }) => {
    const apptType = event.resource?.appointmentType;
    const typeLabel = apptType && apptType !== 'billable' ? getAppointmentTypeLabel(apptType) : null;
    const isDeletable = event.resource?.type === 'appointment'
      && event.resource?.appointmentId
      && event.resource?.status !== 'cancelled'
      && event.resource?.status !== 'completed'
      && onDeleteEventRef.current;
    return (
      <div
        style={{ height: '100%', overflow: 'hidden', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}
        onContextMenu={e => {
          if (onRightClickRef.current) {
            e.preventDefault();
            e.stopPropagation();
            onRightClickRef.current(event, e.clientX, e.clientY);
          }
        }}
        onDoubleClick={e => {
          e.stopPropagation();
          onSelectEventRef.current(event);
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
          {event.title}
          {typeLabel && (
            <span style={{ marginLeft: 4, fontSize: '0.65rem', fontWeight: 600, opacity: 0.85 }}>
              [{typeLabel}]
            </span>
          )}
        </span>
        {isDeletable && (
          <button
            onClick={e => {
              e.stopPropagation();
              onDeleteEventRef.current!(event);
            }}
            style={{ flexShrink: 0, opacity: 0.7, lineHeight: 1, padding: '0 1px' }}
            title="Cancel session"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" style={{ width: 12, height: 12 }}>
              <path fillRule="evenodd" d="M8.75 1A2.75 2.75 0 006 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 10.23 1.482l.149-.022.841 10.518A2.75 2.75 0 007.596 19h4.807a2.75 2.75 0 002.742-2.53l.841-10.52.149.023a.75.75 0 00.23-1.482A41.03 41.03 0 0014 4.193V3.75A2.75 2.75 0 0011.25 1h-2.5zM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4zM8.58 7.72a.75.75 0 00-1.5.06l.3 7.5a.75.75 0 101.5-.06l-.3-7.5zm4.34.06a.75.75 0 10-1.5-.06l-.3 7.5a.75.75 0 101.5.06l.3-7.5z" clipRule="evenodd" />
            </svg>
          </button>
        )}
      </div>
    );
  }, []); // stable — reads callbacks via refs

  const components = useMemo(() => ({ event: CustomEvent }), [CustomEvent]);

  return (
    <div className="coach-calendar-wrapper flex flex-col" style={{ height: '100%' }}>
      {/* Custom toolbar */}
      <div className="flex items-center justify-between px-2 pb-3 flex-shrink-0">
        <div className="flex items-center gap-2">
          <button
            onClick={navigatePrev}
            className="p-1.5 rounded-lg hover:bg-dark-hover text-gray-400 hover:text-white transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          </button>
          <span className="text-sm font-semibold text-gray-200 min-w-[160px] text-center">{dateLabel}</span>
          <button
            onClick={navigateNext}
            className="p-1.5 rounded-lg hover:bg-dark-hover text-gray-400 hover:text-white transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
          </button>
          <button
            onClick={() => { const d = new Date(); setDate(d); onDateChange?.(d); }}
            className="ml-1 px-2.5 py-1 text-xs rounded-lg bg-dark-hover text-gray-400 hover:text-white transition-colors"
          >
            Today
          </button>
        </div>
        {!lockedView && (
          <div className="flex items-center gap-1">
            {(['week', 'month', 'day', 'agenda'] as View[]).map(v => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`px-2.5 py-1 text-xs rounded-lg transition-colors capitalize ${
                  view === v ? 'bg-primary-600 text-white' : 'bg-dark-hover text-gray-400 hover:text-white'
                }`}
              >
                {v}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="flex-1 min-h-0">
      <Calendar
        localizer={localizer}
        events={events}
        view={view}
        date={date}
        onView={setView}
        onNavigate={handleNavigate}
        onSelectSlot={onSelectSlot}
        onSelectEvent={onSelectEvent}
        selectable
        eventPropGetter={eventStyleGetter}
        formats={formats}
        components={components}
        scrollToTime={scrollToTime ?? DEFAULT_SCROLL}
        showMultiDayTimes
        popup
        style={{ height: '100%' }}
        views={['month', 'week', 'day', 'agenda']}
      />
      </div>
    </div>
  );
};

export default CoachCalendarView;
